import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Loading from "../../../../common-components/Loading";
import { getFundingExpenditureDetailsAPI } from "../../../../api/reports-api";
import PaginationBar, {
  usePagination,
  PAGE_SIZE_OPTIONS,
} from "../labor-utilization/Pagination";
import { WaterfallThLabel } from "../waterfall-eac/waterfallTableUtils";
import { useLuTableSortFilter } from "../labor-utilization/tableSortFilterUtils";
import { ctFBtnSt, FilterIcon } from "../scenario-yoy/yoyTableShared";
import {
  getResponse,
  formatDateBySetting,
  converDateFormatForPicker,
} from "../../../../shared-resources/utils";
import {
  TABLE_FONT as FONT,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../../reportTableConfig";

const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];
const MONTH_LABEL_BY_KEY = {
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
};
const MONTH_NUM_BY_KEY = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const thActualStyle = {
  background: TH_ACTUAL_BG,
  color: TH_ACTUAL_TEXT,
  fontWeight: 700,
};

const DT_ROWS = ["Revenue", "Cum Revenue", "Funded Value", "% Spent"];

function compareIdStrings(a, b) {
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

const COLS = [
  {
    key: "contractId",
    label: "Contract ID",
    isText: true,
    getValue: (r) => String(r?.contractId ?? "").trim(),
  },
  {
    key: "clin",
    label: "CLIN",
    isText: true,
    getValue: (r) => String(r?.clin ?? "").trim(),
  },
];

const fmtN = (v) => {
  if (v == null) return "";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `($${abs})` : `$${abs}`;
};

const fmtPct = (v) => (v == null ? "" : `${Number(v).toFixed(1)}%`);

function buildRowCells(entry, columns) {
  const itdRevenue = entry.itdRevenue ?? 0;
  const fundedValue = entry.fundedValue ?? 0;
  const months = entry.months ?? {};

  return DT_ROWS.map((label) => {
    if (label === "Revenue") {
      return {
        label,
        itd: fmtN(itdRevenue),
        monthly: columns.map((m) => fmtN(months[m]?.revenue)),
      };
    }
    if (label === "Cum Revenue") {
      return {
        label,
        itd: fmtN(itdRevenue),
        monthly: columns.map((m) => fmtN(months[m]?.cumRevenue)),
      };
    }
    if (label === "Funded Value") {
      return {
        label,
        itd: "",
        monthly: columns.map(() => fmtN(fundedValue)),
      };
    }
    return {
      label,
      itd: fundedValue ? fmtPct((itdRevenue / fundedValue) * 100) : "",
      monthly: columns.map((m) => fmtPct(months[m]?.pctSpent)),
    };
  });
}

const ID_W = 150;
const CLIN_W = 150;
const REM_W = 130;
const PCT75_W = 100;
const PCT100_W = 100;
const LABEL_W = 140;
const COL_W = 140;

/** Parse an ISO "yyyy-MM-dd" string into a local Date — avoids the UTC-midnight
 *  off-by-one-day shift `new Date(isoString)` can produce in negative-offset zones. */
function parseIsoDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export default function DetailsTab({ classes, filters, onMeta }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [columns, setColumns] = useState(MONTH_KEYS);
  const [columnMeta, setColumnMeta] = useState(null);
  const [viewMode, setViewMode] = useState("YEAR");
  const [duration, setDuration] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(null);
  const [actualThru, setActualThru] = useState(0);
  /** null | { key: "contractId" | "clin", dir: "asc" | "desc" } */
  const [sortState, setSortState] = useState(null);

  const generalSettingsData = useSelector(
    (state) => state.adminReducer.generalSettingsData,
  );
  const dateFormat = converDateFormatForPicker(
    getResponse(generalSettingsData, [])[0]?.date_format || "MM/DD/YYYY",
  );
  const fmtDate = useCallback(
    (v) => {
      const d = parseIsoDate(v);
      return d ? formatDateBySetting(d, dateFormat) : "—";
    },
    [dateFormat],
  );

  const {
    sortedRows: filteredRows,
    hasTableFilters,
    openFilterKey,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
  } = useLuTableSortFilter(COLS, rows);

  const sortKeys = useMemo(() => (sortState ? [sortState] : []), [sortState]);

  const displayRows = useMemo(() => {
    const list = filteredRows;
    if (!sortState) return list;
    return [...list].sort((a, b) => {
      const cmp = compareIdStrings(a[sortState.key], b[sortState.key]);
      return sortState.dir === "asc" ? cmp : -cmp;
    });
  }, [filteredRows, sortState]);

  const {
    pagedItems: pagedRows,
    resetPage,
    ...pagination
  } = usePagination(displayRows, PAGE_SIZE_OPTIONS[0]);

  const filterKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getFundingExpenditureDetailsAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setRows(
            (d.rows || []).map((r, i) => {
              const contractId = String(r?.contractId ?? "").trim();
              return {
                ...r,
                contractId,
                clin: String(r?.clin ?? "").trim(),
                _key: `${contractId}::${i}`,
              };
            }),
          );
          setGrandTotal(d.grandTotal || null);
          // Pruned to the allowed months by monthName/quarter filters — must
          // drive every month-column render below, not the full MONTH_KEYS.
          setColumns(
            Array.isArray(d.columns) && d.columns.length
              ? d.columns
              : MONTH_KEYS,
          );
          setColumnMeta(Array.isArray(d.columnMeta) ? d.columnMeta : null);
          setViewMode(d.viewMode === "DURATION" ? "DURATION" : "YEAR");
          setDuration(d.duration || null);
          setFiscalYear(d.fiscalYear ?? null);
          setActualThru(Number(d.actualThroughMonth) || 0);
          setSortState(null);
          resetPage();
          // DURATION view has no single actualThroughMonth (it can span several
          // fiscal years) — derive the "Actuals through …" label from the last
          // columnMeta entry flagged actual instead.
          const durationActualsLabel = (() => {
            if (d.viewMode !== "DURATION" || !Array.isArray(d.columnMeta))
              return null;
            const lastActual = [...d.columnMeta]
              .reverse()
              .find((c) => c.actual);
            return lastActual ? `Actuals through ${lastActual.label}` : null;
          })();
          onMeta?.({
            actualThroughMonth: d.actualThroughMonth,
            fiscalYear: d.fiscalYear,
            viewMode: d.viewMode,
            actualsLabel:
              d.viewMode === "DURATION" ? durationActualsLabel : undefined,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const makeSortHeaderClick = useCallback(
    (key) => (e) => {
      if (e.target instanceof Element && e.target.closest("[data-filter-btn]"))
        return;
      setSortState((prev) => {
        if (!prev || prev.key !== key) return { key, dir: "desc" }; // first click DESC so order visibly changes Vs typical API ascending order
        if (prev.dir === "desc") return { key, dir: "asc" };
        return null;
      });
    },
    [],
  );

  if (loading) return <Loading />;

  const isDuration = viewMode === "DURATION";
  const showGrandTotal = grandTotal && !hasTableFilters;
  const filterActive =
    openFilterKey === "contractId" || hasFilterValue("contractId");
  const clinFilterActive = openFilterKey === "clin" || hasFilterValue("clin");
  const TABLE_W =
    ID_W +
    CLIN_W +
    REM_W +
    PCT75_W +
    PCT100_W +
    LABEL_W +
    (columns.length + 1) * COL_W;
  // The rendered window can be wider than the CLIN's contractual `duration`
  // (out-of-window revenue, or the pre-2025 lump row widening older CLINs all
  // the way back to 2024-12 — see FUNDING_EXPENDITURE_API.md's Duration mode
  // section) — caption from the actual columnMeta range (first day of the
  // first column's month to the last day of the last column's month) so it
  // never claims a narrower span than what the table columns actually show,
  // formatted as a full date (admin date format) rather than "Mon YYYY".
  const firstColMeta = columnMeta?.[0];
  const lastColMeta = columnMeta?.[columnMeta.length - 1];
  const pad2 = (n) => String(n).padStart(2, "0");
  const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();
  const periodCaption = isDuration
    ? firstColMeta && lastColMeta
      ? `Duration: ${fmtDate(
          `${firstColMeta.year}-${pad2(firstColMeta.month)}-01`,
        )} to ${fmtDate(
          `${lastColMeta.year}-${pad2(lastColMeta.month)}-${pad2(
            lastDayOfMonth(lastColMeta.year, lastColMeta.month),
          )}`,
        )}`
      : `Duration: ${fmtDate(duration?.startDate)} to ${fmtDate(duration?.endDate)}`
    : `FY${fiscalYear ?? ""}`;

  return (
    <div className={classes.dtCard}>
      <div
        style={{ padding: "16px 20px 12px", borderBottom: "1px solid #E2E8F0" }}
      >
        <h2
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#111827",
            letterSpacing: "-0.01em",
            fontFamily: FONT,
            margin: 0,
          }}
        >
          Project-Level Revenue &amp; Funded Value — {periodCaption}
        </h2>
        <p
          style={{
            fontSize: 12,
            fontStyle: "italic",
            color: "#111827",
            fontFamily: FONT,
            margin: "6px 0 0",
          }}
        >
          <span style={{ fontWeight: 600 }}>Note:</span> If the forecast
          indicates that we will not reach 100% of funding, the “100%
          Expenditure Date” will be blank.
        </p>
      </div>
      <div className={classes.dtWrap}>
        <table
          className={classes.dtTable}
          style={{ width: TABLE_W, minWidth: TABLE_W }}
        >
          <colgroup>
            <col style={{ width: ID_W }} />
            <col style={{ width: CLIN_W }} />
            <col style={{ width: REM_W }} />
            <col style={{ width: PCT75_W }} />
            <col style={{ width: PCT100_W }} />
            <col style={{ width: LABEL_W }} />
            {Array(columns.length + 1)
              .fill(0)
              .map((_, i) => (
                <col key={i} style={{ width: COL_W }} />
              ))}
          </colgroup>
          <thead>
            <tr>
              <th
                className={classes.dtThId}
                style={{ cursor: "pointer", zIndex: 6 }}
                onClick={makeSortHeaderClick("contractId")}
                title="Sort by Contract ID"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  <WaterfallThLabel
                    label="Contract ID"
                    colKey="contractId"
                    sortKeys={sortKeys}
                    getLabel={getSortColumnLabel}
                    highlight={openFilterKey === "contractId"}
                    align="left"
                  />
                  <button
                    ref={(el) => {
                      filterBtnRefs.current.contractId = el;
                    }}
                    data-filter-btn
                    type="button"
                    style={ctFBtnSt(filterActive)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilter("contractId", e.currentTarget);
                    }}
                    title="Filter"
                  >
                    <FilterIcon />
                  </button>
                </div>
              </th>
              <th
                className={classes.dtThClin}
                style={{ cursor: "pointer", zIndex: 6 }}
                onClick={makeSortHeaderClick("clin")}
                title="Sort by CLIN"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  <WaterfallThLabel
                    label="CLIN"
                    colKey="clin"
                    sortKeys={sortKeys}
                    getLabel={getSortColumnLabel}
                    highlight={openFilterKey === "clin"}
                    align="left"
                  />
                  <button
                    ref={(el) => {
                      filterBtnRefs.current.clin = el;
                    }}
                    data-filter-btn
                    type="button"
                    style={ctFBtnSt(clinFilterActive)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilter("clin", e.currentTarget);
                    }}
                    title="Filter"
                  >
                    <FilterIcon />
                  </button>
                </div>
              </th>
              <th className={classes.dtThRem}>
                {isDuration
                  ? "Remaining Funding by End of Window"
                  : "Remaining Funding by End of Year"}
              </th>
              <th className={classes.dtThPct75}>75% Expenditure Date</th>
              <th className={classes.dtThPct100}>100% Expenditure Date</th>
              <th className={classes.dtThEmpty} />
              <th className={classes.dtThCol}>ITD</th>
              {columns.map((key, i) => {
                // columnMeta (when present) is the authoritative label/actual source —
                // required in DURATION view, where keys are "yyyy-MM" instead of
                // "jan".."dec" and MONTH_LABEL_BY_KEY/MONTH_NUM_BY_KEY don't apply.
                const meta = columnMeta?.[i];
                const isActual = meta
                  ? !!meta.actual
                  : actualThru > 0 && MONTH_NUM_BY_KEY[key] <= actualThru;
                const label = meta?.label ?? MONTH_LABEL_BY_KEY[key] ?? key;
                return (
                  <th
                    key={key}
                    className={classes.dtThCol}
                    style={isActual ? thActualStyle : undefined}
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((entry, projIdx) => {
              const projId = entry.contractId;
              const isLastProj =
                projIdx === pagedRows.length - 1 && !showGrandTotal;
              const cells = buildRowCells(entry, columns);
              return (
                <React.Fragment key={entry._key || `${projId}-${projIdx}`}>
                  {cells.map((row, rowIdx) => {
                    const isAlt = rowIdx % 2 === 1;
                    const isLastRow = rowIdx === DT_ROWS.length - 1;
                    const groupBorder =
                      !isLastProj && isLastRow
                        ? { borderBottom: "2px solid #CBD5E1" }
                        : isLastRow
                          ? { borderBottom: "1px solid #E2E8F0" }
                          : { borderBottom: "none" };
                    const lblCls = isAlt ? classes.dtTdLblAlt : classes.dtTdLbl;
                    const valCls = isAlt ? classes.dtTdValAlt : classes.dtTdVal;
                    const numCls =
                      row.label === "% Spent" ? classes.dtTdPct : valCls;

                    return (
                      <tr key={`${entry._key || projId}-${row.label}`}>
                        <td
                          className={classes.dtTdId}
                          style={{
                            ...groupBorder,
                            borderBottom: isLastRow
                              ? groupBorder.borderBottom
                              : "none",
                            verticalAlign: "top",
                            paddingTop: rowIdx === 0 ? 10 : 0,
                          }}
                        >
                          {rowIdx === 0 ? projId : ""}
                        </td>
                        <td
                          className={classes.dtTdClin}
                          style={{
                            ...groupBorder,
                            borderBottom: isLastRow
                              ? groupBorder.borderBottom
                              : "none",
                            verticalAlign: "top",
                            paddingTop: rowIdx === 0 ? 10 : 0,
                          }}
                        >
                          {rowIdx === 0 ? entry.clin : ""}
                        </td>
                        <td
                          className={classes.dtTdRem}
                          style={{
                            ...groupBorder,
                            borderBottom: isLastRow
                              ? groupBorder.borderBottom
                              : "none",
                            verticalAlign: "top",
                            paddingTop: rowIdx === 0 ? 10 : 0,
                          }}
                        >
                          {rowIdx === 0
                            ? fmtN(entry.remainingFundingByEndOfYear)
                            : ""}
                        </td>
                        <td
                          className={classes.dtTdPct75}
                          style={{
                            ...groupBorder,
                            borderBottom: isLastRow
                              ? groupBorder.borderBottom
                              : "none",
                            verticalAlign: "top",
                            paddingTop: rowIdx === 0 ? 10 : 0,
                          }}
                        >
                          {rowIdx === 0 ? fmtDate(entry.pct75Date) : ""}
                        </td>
                        <td
                          className={classes.dtTdPct100}
                          style={{
                            ...groupBorder,
                            borderBottom: isLastRow
                              ? groupBorder.borderBottom
                              : "none",
                            verticalAlign: "top",
                            paddingTop: rowIdx === 0 ? 10 : 0,
                          }}
                        >
                          {rowIdx === 0 ? fmtDate(entry.pct100Date) : ""}
                        </td>
                        <td className={lblCls} style={groupBorder}>
                          {row.label}
                        </td>
                        <td className={numCls} style={groupBorder}>
                          {row.itd}
                        </td>
                        {row.monthly.map((v, i) => (
                          <td key={i} className={numCls} style={groupBorder}>
                            {v}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {showGrandTotal &&
              (() => {
                const cells = buildRowCells(grandTotal, columns);
                return cells.map((row, rowIdx) => (
                  <tr key={`total-${rowIdx}`}>
                    <td
                      className={classes.dtTdId}
                      style={{
                        background: "#EEF2FF",
                        borderBottom:
                          rowIdx === DT_ROWS.length - 1
                            ? "1px solid #E2E8F0"
                            : "none",
                        verticalAlign: "top",
                        paddingTop: rowIdx === 0 ? 10 : 0,
                      }}
                    >
                      {rowIdx === 0 ? "Total" : ""}
                    </td>
                    <td
                      className={classes.dtTdClin}
                      style={{
                        background: "#EEF2FF",
                        borderBottom:
                          rowIdx === DT_ROWS.length - 1
                            ? "1px solid #E2E8F0"
                            : "none",
                        verticalAlign: "top",
                        paddingTop: rowIdx === 0 ? 10 : 0,
                      }}
                    />
                    <td
                      className={classes.dtTdRem}
                      style={{ background: "#EEF2FF" }}
                    />
                    <td
                      className={classes.dtTdPct75}
                      style={{ background: "#EEF2FF" }}
                    />
                    <td
                      className={classes.dtTdPct100}
                      style={{ background: "#EEF2FF" }}
                    />
                    <td
                      className={classes.dtTdLbl}
                      style={{ fontWeight: 700, background: "#EEF2FF" }}
                    >
                      {row.label}
                    </td>
                    <td
                      className={
                        row.label === "% Spent"
                          ? classes.dtTdPct
                          : classes.dtTdVal
                      }
                      style={{ fontWeight: 700, background: "#EEF2FF" }}
                    >
                      {row.itd}
                    </td>
                    {row.monthly.map((v, i) => (
                      <td
                        key={i}
                        className={
                          row.label === "% Spent"
                            ? classes.dtTdPct
                            : classes.dtTdVal
                        }
                        style={{ fontWeight: 700, background: "#EEF2FF" }}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ));
              })()}
          </tbody>
        </table>
      </div>
      <PaginationBar
        {...pagination}
        totalItems={displayRows.length}
        onPageChange={pagination.goToPage}
        onPageSizeChange={pagination.handlePageSizeChange}
      />
      {renderFilterPanel()}
    </div>
  );
}
